import { Close } from "@components/buttons/close";
import { ModalRaffleOwnerScheduleForm } from "@components/modals/modal-raffle-owner-schedule-form";
import { ModalRaffleOwnerScheduleSummary } from "@components/modals/modal-raffle-owner-schedule-summary";
import {
  IRaffleResultConfigDTO,
  IRaffleScheduledDrawResponseDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDrawScheduleDTO";
import { RaffleDrawTypeEnum } from "@hooks/enums/raffle";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { FaDice } from "react-icons/fa";
import { FaLandmark } from "react-icons/fa6";
import { useEffect, useMemo, useState } from "react";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  campaign?: string | null;
  raffleKey?: string | null;
  soldPercent: number;
  soldTickets: number;
  totalTickets?: number;
}

function roundToInterval(date: dayjs.Dayjs, interval: number) {
  const minutes = date.minute();
  const nextMinute = Math.ceil(minutes / interval) * interval;

  return date.minute(nextMinute).second(0).millisecond(0);
}

function getDateTimeLocalValue(date: dayjs.Dayjs) {
  return date.format("YYYY-MM-DDTHH:mm");
}

export function ModalRaffleOwnerActions({
  isOpen,
  onClose,
  campaign,
  raffleKey,
  soldPercent,
  soldTickets,
  totalTickets,
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [animate, setAnimate] = useState(false);
  const [lotteryContest, setLotteryContest] = useState("");
  const [lotteryCombination, setLotteryCombination] = useState("5");
  const [randomDate, setRandomDate] = useState("");
  const [activeMethod, setActiveMethod] = useState<"lottery" | "random">(
    "lottery",
  );
  const [config, setConfig] = useState<IRaffleResultConfigDTO>();
  const [scheduleInfo, setScheduleInfo] =
    useState<IRaffleScheduledDrawResponseDTO>({
      hasSchedule: false,
      canCreate: false,
      type: null,
      drawType: null,
      schedule: null,
    });
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [isScheduling, setIsScheduling] = useState<{
    lottery: boolean;
    random: boolean;
  }>({
    lottery: false,
    random: false,
  });

  const canScheduleBySoldPercent =
    !!config && soldPercent >= config.minPercentSold;
  const lotteryInfo = config?.lottery;
  const lotterySourceIsTrusted =
    !!lotteryInfo?.trusted && !!lotteryInfo.nextLotteryContest;
  const nextLotteryContest = lotteryInfo?.nextLotteryContest || 0;
  const maxLotteryContest = config?.allowFutureContestScheduling
    ? nextLotteryContest + config.maxFutureContestsAhead
    : nextLotteryContest;

  const minRandomDate = useMemo(() => {
    if (!config) {
      return roundToInterval(dayjs(), 5);
    }

    return roundToInterval(
      dayjs().add(config.minMinutesToResult, "minute"),
      config.intervalMinTime,
    );
  }, [config]);

  const randomDateIsValid =
    !!randomDate &&
    dayjs(randomDate).isValid() &&
    !dayjs(randomDate).isBefore(minRandomDate);

  const randomMinuteIsValid =
    !!randomDate &&
    !!config &&
    dayjs(randomDate).minute() % config.intervalMinTime === 0;

  const lotteryContestNumber = Number(lotteryContest || 0);
  const lotteryContestIsValid =
    lotterySourceIsTrusted &&
    lotteryContestNumber >= nextLotteryContest &&
    lotteryContestNumber <= maxLotteryContest;
  const lotteryCombinationNumber = Number(lotteryCombination || 0);
  const lotteryCombinationIsValid =
    lotteryCombinationNumber >= 1 && lotteryCombinationNumber <= 5;

  const lotteryMethodIsCompatible =
    !scheduleInfo.drawType ||
    scheduleInfo.drawType === RaffleDrawTypeEnum.FEDERAL_LOTTERY;
  const randomMethodIsCompatible =
    !scheduleInfo.drawType ||
    scheduleInfo.drawType === RaffleDrawTypeEnum.RANDOM_NUMBER;

  const canScheduleLottery =
    lotteryMethodIsCompatible &&
    !!raffleKey &&
    canScheduleBySoldPercent &&
    lotterySourceIsTrusted &&
    lotteryContestIsValid &&
    lotteryCombinationIsValid;
  const canScheduleRandom =
    randomMethodIsCompatible &&
    !!raffleKey &&
    canScheduleBySoldPercent &&
    randomDateIsValid &&
    randomMinuteIsValid;

  async function loadResultConfig() {
    setIsLoadingConfig(true);

    try {
      const response = await api.get("/raffle/default/result/config");
      setConfig(response.data);
      
      if (response.data?.lottery?.nextLotteryContest) {
        setLotteryContest(String(response.data.lottery.nextLotteryContest));
      }
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar a configuração de agendamento.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoadingConfig(false);
    }
  }

  async function loadSchedule() {
    if (!raffleKey) return;

    setIsLoadingSchedule(true);

    try {
      const response = await api.get(
        `/customer/raffle/default/result/schedule?key=${raffleKey}`,
      );
      setScheduleInfo(response.data);
      
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao consultar o agendamento do sorteio.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoadingSchedule(false);
    }
  }

  function handleMethodChange(method: "lottery" | "random") {
    const isCompatible =
      method === "lottery"
        ? lotteryMethodIsCompatible
        : randomMethodIsCompatible;

    if (!isCompatible) {
      pushToastify({
        message:
          method === "lottery"
            ? "Esta campanha foi configurada para Sorteio Aleatório."
            : "Esta campanha foi configurada para Loteria Federal.",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    setActiveMethod(method);
  }

  function handleClose() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleClose();
    }
  }

  async function handleScheduleDraw(type: "lottery" | "random") {
    const isCompatible =
      type === "lottery"
        ? lotteryMethodIsCompatible
        : randomMethodIsCompatible;

    if (!isCompatible) {
      pushToastify({
        message: "O método selecionado é incompatível com o tipo de sorteio da campanha.",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    setIsScheduling((prev) => ({ ...prev, [type]: true }));

    try {
      const response = await api.post(
        "/customer/raffle/default/result/schedule",
        {
          key: raffleKey,
          type,
          ...(type === "lottery"
            ? {
                contestNumber: lotteryContestNumber,
                lotteryCombination: lotteryCombinationNumber,
              }
            : {
                drawAt: dayjs(randomDate).format("YYYY-MM-DDTHH:mm:ss"),
              }),
        },
      );

      pushToastify({
        message: "Sorteio agendado com sucesso.",
        type: "success",
        theme: "dark",
      });
      setScheduleInfo({
        hasSchedule: true,
        canCreate: false,
        type: response.data?.type || null,
        drawType: response.data?.drawType || null,
        schedule: response.data,
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao agendar o sorteio da campanha.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsScheduling((prev) => ({ ...prev, [type]: false }));
    }
  }

  useEffect(() => {
    if (isOpen) {
      setActiveMethod("lottery");
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      setScheduleInfo({
        hasSchedule: false,
        canCreate: false,
        type: null,
        drawType: null,
        schedule: null,
      });
      loadSchedule();
      loadResultConfig();

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }

    setAnimate(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && config) {
      setRandomDate(getDateTimeLocalValue(minRandomDate));
    }
  }, [isOpen, config, minRandomDate]);

  useEffect(() => {
    if (!isOpen || !scheduleInfo.drawType) return;

    setActiveMethod(
      scheduleInfo.drawType === RaffleDrawTypeEnum.RANDOM_NUMBER
        ? "random"
        : "lottery",
    );
  }, [isOpen, scheduleInfo.drawType]);

  if (!isOpen) return null;

  const renderScheduleForm = () => (
    <ModalRaffleOwnerScheduleForm
      campaign={campaign}
      raffleKey={raffleKey}
      soldPercent={soldPercent}
      soldTickets={soldTickets}
      totalTickets={totalTickets}
      config={config}
      isLoadingConfig={isLoadingConfig}
      lotteryContest={lotteryContest}
      lotteryCombination={lotteryCombination}
      randomDate={randomDate}
      minRandomDate={minRandomDate}
      nextLotteryContest={nextLotteryContest}
      maxLotteryContest={maxLotteryContest}
      canScheduleBySoldPercent={canScheduleBySoldPercent}
      lotterySourceIsTrusted={lotterySourceIsTrusted}
      lotteryContestIsValid={lotteryContestIsValid}
      canScheduleLottery={canScheduleLottery}
      canScheduleRandom={canScheduleRandom}
      randomDateIsValid={randomDateIsValid}
      randomMinuteIsValid={randomMinuteIsValid}
      activeMethod={activeMethod}
      lotteryMethodIsCompatible={lotteryMethodIsCompatible}
      randomMethodIsCompatible={randomMethodIsCompatible}
      onMethodChange={handleMethodChange}
      onLotteryContestChange={setLotteryContest}
      onLotteryCombinationChange={setLotteryCombination}
      onRandomDateChange={setRandomDate}
    />
  );

  const activeMethodCanSchedule =
    activeMethod === "lottery" ? canScheduleLottery : canScheduleRandom;
  const activeMethodIsScheduling = isScheduling[activeMethod];
  const activeMethodAction =
    activeMethod === "lottery"
      ? "Agendar pela Loteria Federal"
      : "Agendar sorteio aleatório";

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="raffleOwnerActionsModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl modal-fullscreen-lg-down">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5
                  className="modal-title fw-bold"
                  id="raffleOwnerActionsModalLabel"
                >
                  Agendar Sorteio
                </h5>
                <small className="text-muted">
                  Escolha como o resultado final será definido e acompanhe as
                  validações necessárias para o agendamento.
                </small>
              </div>
              <Close onPress={handleClose} />
            </div>

            <div className="modal-body p-3 p-lg-4">
              {isLoadingSchedule ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <div className="text-muted mt-3">
                    Consultando agendamento...
                  </div>
                </div>
              ) : scheduleInfo.hasSchedule && scheduleInfo.schedule ? (
                <>
                  <ModalRaffleOwnerScheduleSummary
                    type={scheduleInfo.type}
                    schedule={scheduleInfo.schedule}
                  />
                  {scheduleInfo.canCreate && (
                    <div className="mt-3">{renderScheduleForm()}</div>
                  )}
                </>
              ) : scheduleInfo.canCreate ? (
                renderScheduleForm()
              ) : (
                <div className="alert alert-warning mb-0">
                  Esta campanha não permite criar um novo agendamento no
                  momento.
                </div>
              )}
            </div>

            <div className="modal-footer d-flex justify-content-between gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClose}
              >
                Fechar
              </button>
              {!isLoadingSchedule && scheduleInfo.canCreate && (
                <button
                  type="button"
                  className={`btn btn-${
                    activeMethod === "lottery" ? "primary" : "success"
                  }`}
                  disabled={
                    !activeMethodCanSchedule ||
                    isLoadingConfig ||
                    activeMethodIsScheduling
                  }
                  onClick={() => void handleScheduleDraw(activeMethod)}
                >
                  {activeMethod === "lottery" ? (
                    <FaLandmark className="me-2" />
                  ) : (
                    <FaDice className="me-2" />
                  )}
                  {activeMethodIsScheduling
                    ? "Agendando..."
                    : activeMethodAction}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
